import { DatastoresTracking } from "@/lib/tracking/datastores";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  Label,
  Switch,
  toast,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Tooltip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Combobox,
} from "@altimateai/lego";
import { LoadingButton, Stack } from "@altimateai/lego";
import classNames from "classnames";

import { getIconByDatastoreType } from "../DatastoreImage";
import {
  getAvailableDatabases,
  addDatastore,
  syncDatastore,
  testDatastoreConnection,
  toggleDatastoreSyncSchedule,
} from "../../../../helpers/apis";
import styles from "../../styles.module.scss";
import {
  AddDatastoreRequest,
  AddDatabasesRequest,
  Datastore as DatastoreType,
  DatastoreTypeEnum,
} from "../../types";
import datastore_sync from "../../../../assets/icons/datastore_sync.png";
import {
  AddIcon,
  DatastorePlayGrayIcon,
  DatastorePlayIcon,
  DatastoreSyncGrayIcon,
} from "../../../../assets/icons";
import datastore_processing from "../../../../assets/images/datastore_processing.png";
import datastore_success from "../../../../assets/images/datastore_success.svg";
import datastore_fail from "../../../../assets/images/datastore_fail.svg";
import _ from "lodash";
import { ColorTag } from "@/components/tags";
import { stringToNumber } from "@/helpers/utils";
import { API_RESULT_TIMEOUT, TestState } from "../../constants";
import AddTableauCloudSiteDialog from "./AddTableauCloudSiteDialog";

// Tableau Cloud hostnames always end in tableau.com (e.g. 10ay.online.tableau.com),
// matching the backend convention in tableau_workbook_uploader.py and
// tableau_periodic_task_creator.py.
const isTableauCloud = (datastore: DatastoreType): boolean =>
  datastore.type === DatastoreTypeEnum.TABLEAU &&
  (datastore.connection_info?.toLowerCase().includes("tableau.com") ?? false);

const GET_AVAILABLE_DATABASES = "GET_AVAILABLE_DATABASES";
const colors = ["blue", "orange", "yellow", "purple", "dark_green"];

interface DatastoreProps {
  datastore: DatastoreType;
  syncOpAllowed: boolean;
}

interface CredentialItem {
  label: string;
  value: string;
}

const Datastore = ({ datastore, syncOpAllowed }: DatastoreProps): JSX.Element => {
  const [isChecked, setIsChecked] = useState(datastore?.auto_sync || false);
  const [testState, setTestState] = useState<TestState>(TestState.Uninitialized);
  const [showDatabaseSelect, setShowDatabaseSelect] = useState(false);
  const [showAllDatabases, setShowAllDatabases] = useState(false);
  const [databaseSearchTerm, setDatabaseSearchTerm] = useState("");
  const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
  const isTestDisabled = !datastore?.can_sync;
  const queryClient = useQueryClient();

  const { mutate: runTestConnection, isPending: isTestLoading } = useMutation({
    mutationFn: () => testDatastoreConnection(datastore.id),
    onSuccess: response => {
      if (response?.ok) {
        setTestState(TestState.Success);
      } else {
        setTestState(TestState.Fail);
      }
    },
    onError: () => {
      setTestState(TestState.Fail);
    },
    onSettled: () => {
      setTimeout(() => {
        setTestState(TestState.Uninitialized);
      }, API_RESULT_TIMEOUT.DATASTORE);
    },
  });

  const { mutate: runSyncDatastore, isPending: isSyncing } = useMutation({
    mutationFn: () => syncDatastore(datastore.id),
  });

  const { mutate: runToggleSchedule } = useMutation({
    mutationFn: () => toggleDatastoreSyncSchedule(datastore.id),
    onSuccess: res => {
      if (res?.ok) {
        setIsChecked(!isChecked);
      }
    },
  });

  const { data: availableDatabases, isFetching } = useQuery({
    queryKey: [GET_AVAILABLE_DATABASES, datastore.id],
    queryFn: () => getAvailableDatabases(datastore.id.toString()),
    enabled: !!datastore.id,
  });

  const { mutate: createDatastore, isPending: isCreating } = useMutation({
    mutationFn: (data: AddDatastoreRequest | AddDatabasesRequest) => addDatastore(data),
    onSuccess: () => {
      setShowDatabaseSelect(false);
      setSelectedDatabases([]);
      queryClient.invalidateQueries({ queryKey: [GET_AVAILABLE_DATABASES, datastore.id] });
    },
    onError: err => {
      toast({
        variant: "destructive",
        title: "Error",
        description: (err as Error)?.message || "Failed to create datastore",
      });
    },
  });

  const handleToggleSwitch = () => {
    DatastoresTracking.autoSyncToggled(datastore.type, !isChecked);
    runToggleSchedule();
  };

  const handleTestConnection = (e: React.MouseEvent) => {
    e.stopPropagation();
    runTestConnection();
  };

  const handleSync = (e: React.MouseEvent) => {
    e.stopPropagation();
    DatastoresTracking.syncTriggered(datastore.type);
    runSyncDatastore();
  };

  const handleAddDatabases = () => {
    DatastoresTracking.databasesAdded(selectedDatabases.length);
    const data = {
      ...datastore,
      databases: selectedDatabases,
    };
    createDatastore(data);
  };

  const unusedDatabaseOptions = (availableDatabases?.unused_databases || []).map(db => ({
    value: db,
    label: db,
  }));

  const MAX_VISIBLE_DATABASES = 6;
  const MAX_MODAL_RESULTS = 10;

  // For modal: show top 10 when no search, or first 10 matches when searching
  const modalDatabases = (() => {
    if (!availableDatabases?.used_databases) return [];

    if (databaseSearchTerm.trim() === "") {
      // No search term: show first 10 databases
      return availableDatabases.used_databases.slice(0, MAX_MODAL_RESULTS);
    } else {
      // Search term: find first 10 matches
      const matches = [];
      const searchLower = databaseSearchTerm.toLowerCase();

      for (const db of availableDatabases.used_databases) {
        if (db.toLowerCase().includes(searchLower)) {
          matches.push(db);
          if (matches.length >= MAX_MODAL_RESULTS) break;
        }
      }
      return matches;
    }
  })();

  // Get total count of matches for display purposes
  const totalMatches =
    databaseSearchTerm.trim() === ""
      ? availableDatabases?.used_databases.length || 0
      : availableDatabases?.used_databases.filter(db =>
          db.toLowerCase().includes(databaseSearchTerm.toLowerCase())
        ).length || 0;

  const visibleDatabases = availableDatabases?.used_databases.slice(0, MAX_VISIBLE_DATABASES) || [];
  const remainingCount = (availableDatabases?.used_databases.length || 0) - MAX_VISIBLE_DATABASES;

  function extractCredentials(apiResponse: DatastoreType): CredentialItem[] {
    let credentialData: CredentialItem[] = [];
    let account = null;
    let database = null;
    let role = null;
    let warehouse = null;
    let server = null;

    switch (apiResponse.type) {
      case DatastoreTypeEnum.S3: {
        const s3ConnectionParts = apiResponse.connection_info?.split("/") || [];
        const s3AccountParts = s3ConnectionParts[0]?.split(".") || [];
        account = s3AccountParts[0];
        database = s3ConnectionParts[1]?.split("?")[0];
        break;
      }
      case DatastoreTypeEnum.POSTGRES: {
        const postgresConnectionParts = apiResponse.connection_info?.split(":") || [];
        const postgresHostParts = postgresConnectionParts[0]?.split(".") || [];
        account = postgresHostParts[0];
        database = postgresConnectionParts[postgresConnectionParts.length - 1]?.split("/")[1];
        break;
      }
      case DatastoreTypeEnum.SNOWFLAKE: {
        const connectionParams = apiResponse.connection_info?.split("/") || [];
        account = connectionParams[0];
        const queryParts = connectionParams[1]?.split("?") || [];
        database = queryParts[0];
        const queryParams = new URLSearchParams(queryParts[1] || "");
        role = queryParams.get("role");
        warehouse = queryParams.get("warehouse");
        break;
      }
      case DatastoreTypeEnum.TABLEAU: {
        const connectionParams = apiResponse.connection_info?.split("/") || [];
        if (connectionParams.length > 1) {
          server = connectionParams[0];
        }
        break;
      }
      default: {
        break;
      }
    }

    if (account) {
      credentialData.push({
        label: "Account",
        value: account,
      });
    }
    if (role) {
      credentialData.push({
        label: "Role",
        value: role,
      });
    }
    if (database) {
      credentialData.push({
        label: "Database",
        value: database,
      });
    }
    if (warehouse) {
      credentialData.push({
        label: "Warehouse",
        value: warehouse,
      });
    }
    if (server) {
      credentialData.push({
        label: "Server",
        value: `https://${server}`,
      });
    }

    return credentialData;
  }

  const credentialData = extractCredentials(datastore);

  return (
    <Stack className="al-grid al-grid-cols-[2fr_3fr] al-gap-6 al-w-full al-mt-6">
      <Card>
        <CardContent className={styles.details_left}>
          <div>
            <div className={classNames("mb-2", styles.icon_row)}>
              <Stack direction="row" justify="between" align="center">
                <Stack direction="row" justify="between" align="center">
                  {getIconByDatastoreType({
                    datastoreType: datastore.type,
                    className: styles.datastore_icon,
                  })}
                  <div className="ms-2">
                    <div className="al-text-lg al-font-semibold">{datastore.name}</div>
                    <div className={styles.subtext}>{_.capitalize(datastore?.type)}</div>
                  </div>
                </Stack>
                <div className={styles.right_column}>
                  {datastore?.can_sync && (
                    <div className={classNames(styles.switch_container)}>
                      <Label htmlFor={"flexSwitchCheckDefault" + datastore.id}>Enable Sync</Label>

                      <Tooltip
                        title={!syncOpAllowed ? "Insufficient permissions for enabling sync" : ""}
                      >
                        <Switch
                          id={"flexSwitchCheckDefault" + datastore.id}
                          checked={isChecked}
                          onCheckedChange={handleToggleSwitch}
                          disabled={!syncOpAllowed}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              </Stack>
            </div>
            <div className={classNames("mb-2 text-muted fw-400", styles.description)}>
              {datastore?.description}
            </div>
          </div>
          <div className={styles.sync_details}>
            <Stack direction="row" justify="between" align="center" className="mt-1">
              <Stack direction="row" justify="between" align="center">
                {datastore?.can_sync && syncOpAllowed ? (
                  <Button
                    variant="ghost"
                    className={classNames(styles.inside_card, "me-2")}
                    onClick={handleSync}
                    aria-label="Sync datastore now"
                  >
                    {isSyncing ? (
                      <>
                        <img src={datastore_sync} alt="syncing" className={styles.rotateIcon} />
                        <div className={styles.title}>Syncing...</div>
                      </>
                    ) : (
                      <>
                        <img src={datastore_sync} alt="sync_now" />
                        <div className={styles.title}>Sync Now</div>
                      </>
                    )}
                  </Button>
                ) : (
                  <Tooltip
                    title={
                      !syncOpAllowed
                        ? "Insufficient permissions for performing sync"
                        : "Credentials are not added or synced to SaaS UI"
                    }
                  >
                    <Button
                      variant="ghost"
                      className={classNames(styles.inside_card, "me-2")}
                      disabled
                      aria-label="Sync datastore now (disabled)"
                    >
                      <DatastoreSyncGrayIcon />
                      <div className={styles.title}>Sync Now</div>
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Test Connection">
                  <Button
                    variant="ghost"
                    className={classNames(styles.inside_card, {
                      [styles.no_pointer_events]: isTestDisabled,
                    })}
                    onClick={handleTestConnection}
                    disabled={isTestDisabled}
                    aria-label="Test datastore connection"
                  >
                    {isTestLoading ? (
                      <img
                        src={datastore_processing}
                        className={styles.rotateIcon}
                        alt="test_connection"
                      />
                    ) : (
                      <>
                        {testState === TestState.Uninitialized &&
                          (isTestDisabled ? <DatastorePlayGrayIcon /> : <DatastorePlayIcon />)}
                        {testState === TestState.Success && (
                          <img src={datastore_success} alt="test_connection" />
                        )}
                        {testState === TestState.Fail && (
                          <img src={datastore_fail} alt="test_connection" />
                        )}
                      </>
                    )}
                    <div className={styles.title}>Test</div>
                  </Button>
                </Tooltip>
              </Stack>
              {testState === TestState.Success && (
                <div className={styles.success_text}>Success</div>
              )}
              {testState === TestState.Fail && (
                <div className={styles.fail_text}>Failed, Try Again</div>
              )}
            </Stack>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className={classNames(styles.credentials)}>
          <Stack direction="column" className="gap-lg">
            <div className={styles.title}>Details</div>
            <Stack direction="row" align="center" className="gap-xl">
              {credentialData.map((item, i) => {
                if (item.label === "Database") {
                  return null;
                }
                return (
                  <Stack key={i} direction="row">
                    <div className={styles.credential_title}>{item.label}:&nbsp;</div>
                    <div className={styles.credential_value}>{item.value}</div>
                  </Stack>
                );
              })}
            </Stack>
            <Stack direction="column" className="gap-md">
              <Stack direction="row" align="center" className="gap-xl">
                <div className={styles.credential_title}>
                  {datastore.type === "tableau" ? "Sites" : "Databases"}&nbsp;
                </div>
                {isFetching ? (
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : availableDatabases ? (
                  <Stack direction="row" align="center" className="gap-2">
                    <Stack direction="row" wrap className="gap-2">
                      {visibleDatabases.map(db => (
                        <ColorTag
                          key={db}
                          color={colors[stringToNumber(db, colors.length)]}
                          label={db}
                        />
                      ))}

                      {remainingCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="al-text-sm al-text-muted-foreground al-hover:text-foreground"
                          onClick={() => setShowAllDatabases(true)}
                        >
                          +{remainingCount} more
                        </Button>
                      )}

                      {isTableauCloud(datastore) ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Add site"
                            className={styles.plus_button}
                            onClick={() => setShowDatabaseSelect(true)}
                          >
                            <AddIcon className={styles.plus_icon} />
                          </Button>
                          <AddTableauCloudSiteDialog
                            datastore={datastore}
                            open={showDatabaseSelect}
                            onOpenChange={setShowDatabaseSelect}
                            availableDatabasesQueryKey={[GET_AVAILABLE_DATABASES, datastore.id]}
                          />
                        </>
                      ) : (
                        <Popover open={showDatabaseSelect} onOpenChange={setShowDatabaseSelect}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Add databases"
                              className={styles.plus_button}
                            >
                              <AddIcon className={styles.plus_icon} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className={styles.database_select_tooltip}>
                            <Stack direction="column" className="gap-sm p-3">
                              <Combobox
                                multiSelect
                                value={selectedDatabases}
                                onChange={(newValue: string[]) => {
                                  setSelectedDatabases(Array.isArray(newValue) ? newValue : []);
                                }}
                                placeholder={
                                  datastore.type === DatastoreTypeEnum.TABLEAU
                                    ? "Select sites to add"
                                    : "Select databases to add"
                                }
                                buttonProps={{ className: "al-w-full al-text-gray" }}
                                options={unusedDatabaseOptions}
                              />
                              <Stack direction="row" justify="end" className="gap-sm">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="al-text-black"
                                  onClick={() => {
                                    setShowDatabaseSelect(false);
                                    setSelectedDatabases([]);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <LoadingButton
                                  variant="default"
                                  size="sm"
                                  onClick={handleAddDatabases}
                                  disabled={selectedDatabases.length === 0}
                                  loading={isCreating}
                                >
                                  Apply
                                </LoadingButton>
                              </Stack>
                            </Stack>
                          </PopoverContent>
                        </Popover>
                      )}
                    </Stack>

                    {/* All Databases Modal */}
                    <Dialog
                      open={showAllDatabases}
                      onOpenChange={open => {
                        setShowAllDatabases(open);
                        if (!open) {
                          setDatabaseSearchTerm(""); // Reset search when modal closes
                        }
                      }}
                    >
                      <DialogContent className="al-max-w-2xl al-max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle>
                            {databaseSearchTerm.trim() === "" ? (
                              <>
                                {datastore.type === "tableau" ? "Sites" : "Databases"}
                                (showing {modalDatabases.length} of {totalMatches})
                              </>
                            ) : (
                              <>
                                Search Results (showing {modalDatabases.length} of {totalMatches}{" "}
                                matches)
                              </>
                            )}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="al-space-y-4">
                          <Input
                            placeholder={`Search ${datastore.type === "tableau" ? "sites" : "databases"}...`}
                            value={databaseSearchTerm}
                            onChange={e => setDatabaseSearchTerm(e.target.value)}
                            className="al-w-full"
                          />
                          <div className="al-max-h-96 al-overflow-y-auto al-space-y-2">
                            {modalDatabases.length > 0 ? (
                              <>
                                <div className="al-grid al-grid-cols-1 al-gap-2">
                                  {modalDatabases.map((db: string) => (
                                    <Stack
                                      key={db}
                                      direction="row"
                                      justify="between"
                                      align="center"
                                      className="al-p-3 al-border al-rounded-md al-hover:bg-muted"
                                    >
                                      <span className="al-font-medium">{db}</span>
                                      <ColorTag
                                        color={colors[stringToNumber(db, colors.length)]}
                                        label=""
                                      />
                                    </Stack>
                                  ))}
                                </div>
                                {totalMatches > MAX_MODAL_RESULTS && (
                                  <div className="al-text-center al-text-muted-foreground al-text-sm al-py-2">
                                    {databaseSearchTerm.trim() === ""
                                      ? `Showing first ${MAX_MODAL_RESULTS} of ${totalMatches} ${datastore.type === "tableau" ? "sites" : "databases"}`
                                      : `Showing first ${MAX_MODAL_RESULTS} matches. Refine your search to see more specific results.`}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="al-text-center al-text-muted-foreground al-py-8">
                                {databaseSearchTerm
                                  ? `No ${datastore.type === "tableau" ? "sites" : "databases"} found matching "${databaseSearchTerm}"`
                                  : `No ${datastore.type === "tableau" ? "sites" : "databases"} available`}
                              </div>
                            )}
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default Datastore;
